<script setup>
import { nextTick, onMounted, ref, watch } from 'vue'
import debounce from 'lodash.debounce'
import LibraryModal from './LibraryModal.vue'
import { ApiService } from '@/js/services/api'

const emit = defineEmits(['update'])

const props = defineProps({
  filters: Object,
  api: String,
})

const books = ref({})
const table = ref({
  perPage: 10,
  page: 1,
  loading: false,
  ordering: 'titlu',
})

const modal = ref({ active: false, book: null })

const getBooks = async () => {
  table.value.loading = true
  const query = []

  Object.entries(props.filters)
    .filter(([k, v]) => v && v.length)
    .forEach(([k, v]) => {
      if (k == 'autori')
        v.forEach((e) => {
          query.push([k, e._id])
        })
      else query.push([k, Array.isArray(v) ? v.map((e) => e._id) : v])
    })

  books.value = await ApiService[props.api]([
    ['page_size', table.value.perPage],
    ['page', table.value.page],
    ['ordering', table.value.ordering],
    ...query,
  ])

  table.value.loading = false
  emit('update', books.value.count)
}

const openModal = (book) => {
  modal.value = {
    active: true,
    book,
  }
}

const onPageChange = (page) => {
  table.value.page = page
  getBooks()
}

const onSort = (field, order) => {
  table.value.ordering = (order == 'desc' ? '-' : '') + field
  getBooks()
}

watch(
  () => props.filters,
  debounce(() => {
    table.value.page = 1
    getBooks()
  }, 250),
  {
    deep: true,
  }
)

onMounted(() => {
  if (props.api) getBooks()

  // Buefy's mobile-sort UI renders a bare <select> and an icon-only
  // direction button with no accessible names (WCAG 4.1.2) — label them.
  nextTick(() => {
    document
      .querySelectorAll('.table-mobile-sort select:not([aria-label])')
      .forEach((el) => el.setAttribute('aria-label', 'Sortează după'))
    document
      .querySelectorAll('.table-mobile-sort button:not([aria-label])')
      .forEach((el) =>
        el.setAttribute('aria-label', 'Schimbă direcția de sortare')
      )
  })
})
</script>

<template>
  <b-table
    :data="books.results"
    :paginated="books?.count > 10"
    backend-pagination
    backend-sorting
    :total="books.count"
    :per-page="table.perPage"
    @page-change="onPageChange"
    @sort="onSort"
    :loading="table.loading"
    striped
    pagination-order="is-centered"
    aria-next-label="Pagina următoare"
    aria-previous-label="Pagina anterioară"
    aria-page-label="Pagina"
    aria-current-label="Pagina curentă"
    mobile-sort-placeholder="Sortează după"
  >
    <b-table-column
      label="#"
      width="70"
      centered
      v-slot="props"
      cell-class="has-text-grey"
    >
      {{ props.index + table.perPage * (table.page - 1) + 1 }}
    </b-table-column>

    <b-table-column field="titlu" label="Titlu" sortable v-slot="props">
      <div>
        <p class="is-size-6 has-text-weight-semibold">
          {{ props.row.titlu }}
        </p>
        <p class="is-size-7 has-text-grey">de {{ props.row.autori_str }}</p>
      </div>
    </b-table-column>

    <b-table-column
      field="domeniu__titlu"
      label="Domeniu"
      width="200"
      sortable
      v-slot="props"
      cell-class="has-text-grey"
    >
      {{ props.row.domeniu }}
    </b-table-column>

    <slot>
      <b-table-column
        field="carti"
        label="Cărți disponibile"
        width="150"
        v-slot="props"
        cell-class="has-text-grey"
      >
        {{ props.row.carti_disponibile }}
      </b-table-column>

      <b-table-column
        width="100"
        sortable
        centered
        v-slot="props"
        cell-class="has-text-right"
      >
        <a @click.prevent="openModal(props.row)" class="has-text-weight-medium">
          <span>Detalii</span>
          <b-icon icon="arrow-right" size="is-size-7" />
        </a>
      </b-table-column>
    </slot>
  </b-table>

  <LibraryModal
    v-model="modal.active"
    v-bind="{ book: modal.book }"
    v-if="modal.book"
  />
</template>
